import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import notificationService from '@/shared/services/notificationService';
import type { Notification, NotificationListResponse } from '@/shared/types/notification';

interface NotificationState {
  notifications: Notification[];
  unreadCount: number;
  total: number;
  loading: boolean;
  error: string | null;
}

const initialState: NotificationState = {
  notifications: [],
  unreadCount: 0,
  total: 0,
  loading: false,
  error: null,
};

/**
 * 알림 목록 조회
 */
export const fetchNotifications = createAsyncThunk(
  'notifications/fetchNotifications',
  async (params?: { limit?: number; offset?: number; is_read?: boolean }) => {
    const response = await notificationService.getNotifications(params);
    return response;
  }
);

/**
 * 읽지 않은 알림 개수 조회
 */
export const fetchUnreadCount = createAsyncThunk('notifications/fetchUnreadCount', async () => {
  const count = await notificationService.getUnreadCount();
  return count;
});

/**
 * 알림 읽음 처리
 */
export const markAsRead = createAsyncThunk(
  'notifications/markAsRead',
  async (notificationId: string) => {
    const notification = await notificationService.markAsRead(notificationId);
    return notification;
  }
);

/**
 * 모든 알림 읽음 처리
 */
export const markAllAsRead = createAsyncThunk('notifications/markAllAsRead', async () => {
  await notificationService.markAllAsRead();
});

/**
 * 알림 삭제
 */
export const deleteNotification = createAsyncThunk(
  'notifications/deleteNotification',
  async (notificationId: string) => {
    await notificationService.deleteNotification(notificationId);
    return notificationId;
  }
);

const notificationSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    clearNotifications: (state) => {
      state.notifications = [];
      state.unreadCount = 0;
      state.total = 0;
      state.error = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // fetchNotifications
    builder
      .addCase(fetchNotifications.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchNotifications.fulfilled,
        (state, action: PayloadAction<NotificationListResponse>) => {
          state.loading = false;
          state.notifications = action.payload.notifications;
          state.unreadCount = action.payload.unreadCount;
          state.total = action.payload.total;
        }
      )
      .addCase(fetchNotifications.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch notifications';
      });

    // fetchUnreadCount
    builder
      .addCase(fetchUnreadCount.pending, (state) => {
        state.error = null;
      })
      .addCase(fetchUnreadCount.fulfilled, (state, action: PayloadAction<number>) => {
        state.unreadCount = action.payload;
      })
      .addCase(fetchUnreadCount.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to fetch unread count';
      });

    // markAsRead
    builder
      .addCase(markAsRead.pending, (state) => {
        state.error = null;
      })
      .addCase(markAsRead.fulfilled, (state, action: PayloadAction<Notification>) => {
        const index = state.notifications.findIndex((n) => n.id === action.payload.id);
        if (index !== -1) {
          state.notifications[index] = action.payload;
          if (action.payload.is_read) {
            state.unreadCount = Math.max(0, state.unreadCount - 1);
          }
        }
      })
      .addCase(markAsRead.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to mark as read';
      });

    // markAllAsRead
    builder
      .addCase(markAllAsRead.pending, (state) => {
        state.error = null;
      })
      .addCase(markAllAsRead.fulfilled, (state) => {
        state.notifications = state.notifications.map((n) => ({ ...n, is_read: true }));
        state.unreadCount = 0;
      })
      .addCase(markAllAsRead.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to mark all as read';
      });

    // deleteNotification
    builder
      .addCase(deleteNotification.pending, (state) => {
        state.error = null;
      })
      .addCase(deleteNotification.fulfilled, (state, action: PayloadAction<string>) => {
        const deletedNotification = state.notifications.find((n) => n.id === action.payload);
        state.notifications = state.notifications.filter((n) => n.id !== action.payload);
        state.total = Math.max(0, state.total - 1);
        if (deletedNotification && !deletedNotification.is_read) {
          state.unreadCount = Math.max(0, state.unreadCount - 1);
        }
      })
      .addCase(deleteNotification.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to delete notification';
      });
  },
});

export const { clearNotifications, clearError } = notificationSlice.actions;
export default notificationSlice.reducer;
